import React, { Component, Fragment } from 'react'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  checkPermission,
  getUTCTime,
  utcToLocal,
  buildAvatars,
  checkCanViewAppraiserNameOnOrder,
  getRUCCTypeFromCode,
  shouldClearSelectedTemplate,
  canApproveConversationMessages,
} from '../../common/utils/helpers'
import { FORM_KEYS_TO_READABLE } from '../../common/constants/forms'
import { buildFieldLabelMap } from '../../common/constants/queryBuilder'
import { Icon as LegacyIcon } from '@ant-design/compatible'
import { BarsOutlined, FormOutlined, ProfileOutlined } from '@ant-design/icons'
import { Tag, Popover, Button } from 'antd'
import moment from 'moment'
import { convertUTCToLocalTime } from '@helpers/dateHelpers'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { fetchInspectionAvailability } from '../../store/reducers/order/orderSlice'
import { selectInspectionAvailabilityData } from '../../store/reducers/order/orderSelectors'

import ModalTrigger from '../../common/modals/ModalTrigger'
import { ChatWrapper } from '../../common/widgets/ChatWrapper'
import EditAssignedModal from './EditAssignedModal'
import EditAMCManagementFeeModal from './EditAMCManagementFeeModal'
import EditVendorFeeOverrideModal from './EditVendorFeeOverrideModal'
import UnansweredMessageInfo from './UnansweredMessageInfo'
import CustomTimelineItem from './CustomTimelineItem'
import Notes from './Notes'
import { history } from '../../init'
import { paymentOptionsToReadable } from '../../common/constants/borrowerPaymentOptions'
import { SmallLoader } from '../../common/layout/components/Loader'
import CommentTemplateDropdown from './CommentTemplateDropdown'
import { isPdrOrStreamlinedInspectionOrder, isStreamlinedInspectionOrCompletionReportOrder } from '../../../helpers/pdr_orders'
import Rephrase from '../../common/utils/Rephrase'
import { formatCurrency } from '@common/formatters/currency'
import { JOB_TYPES } from '@app/common/constants/jobTypes'
import { REQUEST_METHODS, STATUS_KEY_FINDING_APPRAISERS, STATUS_KEY_SCHEDULING_INSPECTION } from '@app/common/constants/orders'
import { prepConversationMessages } from '@helpers/conversations'
import ModalSetInspectionDate from './ModalSetInspectionDate'
import ChatSelector from './NewMessageContainer/ChatSelector'
import InspectionAvailabilityModal from './InspectionAvailability'
import { getAudienceFilter } from '@app/pages/adminSettings/MessageTemplateManagement/MessageTemplates/helpers'

const CheckableTag = Tag.CheckableTag
const MESSAGE_FILTER = 'Messages'
const STATUS_FILTER = 'Statuses'
const REMINDER_FILTER = 'Reminders'

function getOrderStatus(orderData, is_amc_lender) {
  const { revision_status, job_type } = orderData
  // Copied this logic from renderStatusBadge in helpers.js.
  // TODO: Add redux selectors to provide a consistent way
  // of selecting data from the store.
  if (revision_status && (job_type !== JOB_TYPES.AMC || is_amc_lender)) {
    return orderData.revision_status
  }

  return orderData.status === 'Pending Review' ? 'Submitted' : orderData.status
}

function getInspectionDateAndTime(inspectionDate, timezone) {
  if (!inspectionDate) { return '' }

  if (timezone) {
    return convertUTCToLocalTime(inspectionDate, timezone).format('MM/DD hh:mm A zz')
  }

  return getUTCTime(inspectionDate)
}

function createTimelineEvent(
  description,
  subDescription,
  iconType,
  iconColor,
  tagText,
  additionalInfo,
  viewReasonText,
  initiator,
  initiator_type,
  tooltipPermission,
  hideTooltip,
  amcLender,
  amcOrder,
  orderData,
  eventIndex,
  extraHtml,
) {
  const key = `${description}${subDescription}${eventIndex}`

  return (
    <CustomTimelineItem
      key={key}
      dot={<LegacyIcon type={iconType} />}
      color={iconColor}
      description={description}
      subDescription={subDescription}
      tagText={tagText}
      additionalInfo={additionalInfo}
      viewReasonText={viewReasonText}
      initiator={initiator}
      initiator_type={initiator_type}
      tooltipPermission={tooltipPermission}
      hideTooltip={hideTooltip}
      amcLender={amcLender}
      amcOrder={amcOrder}
      orderData={orderData}
      extraHtml={extraHtml}
    />
  )
}

const createAMCTimeline = (
  te,
  orderData,
  shouldGetReminders,
  shouldGetStatuses,
  shouldGetMessages,
  is_amc_lender,
  refreshOrder,
  eventIndex
) => {
  let eventDesc = te.event_desc
  let additionalInfo = te.additional_info
  const reminder = te.reminder
  let viewReasonText
  // eslint-disable-next-line no-unused-vars
  let messageStatus = false
  let tooltipPermission = false
  let hideTooltip = false
  const iconGreen = '#52C41B'
  let subText
  const format = 'MMM Do YYYY hh:mm a'
  let tagText
  let iconType = 'check-circle-o' // Default the icon type to check circle.
  let iconColor = iconGreen // Default the icon color to green
  if (te.created) {
    subText = utcToLocal(te.created).format(format)
  } else if (te.time) {
    subText = utcToLocal(te.time).format(format)
  }

  switch (te.notification_type) {
    case 'order_inspection_date_update':
      additionalInfo = utcToLocal(additionalInfo).format(format)
      viewReasonText = 'View Inspection Date'
      break

    // This case is the first time the inspection is scheduled (the status change)
    case 'order_status_change':
      if (eventDesc === 'Inspection Scheduled') {
        additionalInfo = utcToLocal(additionalInfo).format(format)
        viewReasonText = 'View Inspection Date'
      }
      break
    case 'order_inspection_scheduled':
      eventDesc = 'Inspection Scheduled'
      additionalInfo = utcToLocal(additionalInfo).format(format)
      viewReasonText = 'View Inspection Date'
      break
    case 'order_company_assignment_decline':
      viewReasonText = 'View Reason'
      iconType = 'exclamation-circle'
      iconColor = 'grey'
      break
    case 'order_canceled':
      iconType = 'close-circle-o'
      iconColor = 'red'
      if (orderData.has_been_reassigned) {
        eventDesc = 'Order Reassigned'
        iconType = 'exclamation-circle-o'
        iconColor = '#faad14'
        hideTooltip = true
        additionalInfo = 'reassigned'
        viewReasonText = (
          <span
            className="pointer"
            onClick={() => history.goBack()}
            id={additionalInfo}
          >
            {' '}
            Return to Active Order
          </span>
        )
      }
      break
    case 'order_reassigned':
      tooltipPermission = true
      if (additionalInfo) {
        hideTooltip = true
        viewReasonText = (
          <span
            className="pointer"
            onClick={() => history.push(`/orders/${additionalInfo}`)}
            id={additionalInfo}
          >
            {' '}
            View Reassigned Order
          </span>
        )
        /* viewReasonText = <span onClick={() => {
          history.replace({ pathname: `/orders/${additionalInfo}` });
          // refreshOrder()
        }}>
          View Reassigned Order
        </span> */
      }
      break
    case 'order_cancel_request':
      iconType = 'close-circle-o'
      iconColor = 'red'
      viewReasonText = 'View Reason'
      break
    case 'order_hold_change':
      iconType = 'exclamation-circle'
      iconColor = 'grey'
      break
    case 'order_placed_on_hold':
      iconType = 'exclamation-circle'
      iconColor = 'grey'
      viewReasonText = 'View Reason'
      break
    case 'initial_message':
      eventDesc = 'There are no messages for this order yet.'
      subText = "Type in the box below and click 'Send' to send a message."
      iconType = 'message'
      iconColor = 'grey'
      messageStatus = true
      break
    case 'Inspection Date':
      iconType = 'clock-circle-o'
      iconColor = 'red'
      eventDesc = 'Inspection Date'
      tagText = 'Behind Schedule'
      break
    case 'Order Due Date':
      iconType = 'clock-circle-o'
      iconColor = 'red'
      eventDesc = 'Order Due Date'
      tagText = 'Behind Schedule'
      break
    case 'order_company_assignment':
      viewReasonText = 'Assigned'
      tooltipPermission = true
      // We dont want to show appraiser name to users that dont have permission
      // (the permission check in CustomTimelineItem doesnt catch this event because this event is initiated by the lender)
      if (!checkCanViewAppraiserNameOnOrder(orderData)) {
        hideTooltip = true
        viewReasonText = null
      }
      break
    case 'loan_branch_updated':
      viewReasonText = 'More Details'
      tooltipPermission = true
      break
    case 'broadcast_company_request':
      viewReasonText = 'Company Names'
      additionalInfo = (
        <ul>
          {additionalInfo.split('\n').map((name) => (
            <li
              key={name}
              className="p-0 mb-0"
              style={{ listStyleType: 'none' }}
            >
              {name}
            </li>
          ))}
        </ul>
      )
      tooltipPermission = true
      break
    case 'borrower_payment_refund_status_update':
      viewReasonText = 'Refund Status'
      if (additionalInfo && additionalInfo === 'failed') {
        iconType = 'exclamation-circle'
        iconColor = 'red'
      }
      break
    case 'appraiser_notified_of_payment':
      viewReasonText = 'View Message'
      break
    case 'lender_attention_resolved':
      viewReasonText = 'View Reasons'
      break
    case 'p_s_upload':
    case 'evault_upload':
    case 'amc_evault_upload':
      viewReasonText = 'Document Name'
      break
    case 'consumer_payment_error':
      iconType = 'exclamation-circle'
      iconColor = 'red'
      break
    case 'order_offer':
      subText = utcToLocal(te.created).format(format)
      break
    case 'consumer_submission_download_alerts':
      iconType = 'exclamation-circle'
      iconColor = 'red'
      if (additionalInfo) {
        viewReasonText = 'View Reason'
      }
      break
    case 'order_fee_escalation_denied':
    case 'order_due_date_denied':
    case 'order_fee_escalation_and_due_date_denied':
    case 'order_counter_denied':
      viewReasonText = 'More Details'
      iconType = 'exclamation-circle'
      iconColor = 'grey'
      break
    case 'order_fee_escalation_accepted':
    case 'order_due_date_accepted':
    case 'order_fee_escalation_and_due_date_accepted':
    case 'order_counter_approved':
      viewReasonText = 'More Details'
      break
    case 'internal_loan_transfer':
      viewReasonText = 'More Details'
      break
    default:
      if (!eventDesc) {
        eventDesc = te.notification_type
      }
  }

  if (reminder && shouldGetReminders) {
    iconType = 'bell'
    iconColor = 'grey'
    return createTimelineEvent(
      eventDesc,
      subText,
      iconType,
      iconColor,
      tagText,
      additionalInfo,
      viewReasonText,
      te.initiator,
      te.initiator_type,
      tooltipPermission,
      hideTooltip,
      is_amc_lender,
      orderData.job_type === JOB_TYPES.AMC,
      orderData,
      eventIndex,
      te.extra_html,
    )
  }

  if (!reminder && shouldGetStatuses) {
    return createTimelineEvent(
      eventDesc,
      subText,
      iconType,
      iconColor,
      tagText,
      additionalInfo,
      viewReasonText,
      te.initiator,
      te.initiator_type,
      tooltipPermission,
      hideTooltip,
      is_amc_lender,
      orderData.job_type === JOB_TYPES.AMC,
      orderData,
      eventIndex,
      te.extra_html,
    )
  } else if (messageStatus && shouldGetMessages) {
    return createTimelineEvent(
      eventDesc,
      subText,
      iconType,
      iconColor,
      tagText,
      additionalInfo,
      viewReasonText,
      te.initiator,
      te.initiator_type,
      tooltipPermission,
      hideTooltip,
      is_amc_lender,
      orderData.job_type === JOB_TYPES.AMC,
      orderData,
      eventIndex,
      te.extra_html,
    )
  }
}

function dateSort(a, b) {
  if (moment(a.created).isAfter(b.created)) {
    return 1
  } else if (moment(a.created).isBefore(b.created)) {
    return -1
  } else {
    return 0
  }
}

function getDeliveryTimelineEvents(orderData, shouldBeVendorNeutral) {
  const buildingConsumerEvents = []

  if (orderData.submission_sent_to_consumer) {
    orderData.submission_sent_to_consumer.forEach((event) => {
      let name = ''
      const option = event.consumer
      if (option.full_name) {
        name = option.full_name
      } else {
        name = option.firstname + ' ' + option.lastname
      }
      if ('is_sent_manually' in event && event.is_sent_manually) {
        const displayText = `Manually sent ${shouldBeVendorNeutral ? 'report' : 'appraisal'} to ${name}`
        buildingConsumerEvents.push({
          notification_type: displayText,
          created: event.time,
          initiator: event.initiator,
          initiator_type: 'lender',
        })
      } else {
        const displayText = `Sent ${shouldBeVendorNeutral ? 'report' : 'appraisal'} to ${name}`
        buildingConsumerEvents.push({
          notification_type: displayText,
          created: event.time,
        })
      }
    })
  }

  if (orderData.submission_downloaded) {
    orderData.submission_downloaded.forEach((event) => {
      let name = ''
      const option = event.consumer
      if (option.full_name) {
        name = option.full_name
      } else {
        name = option.firstname + ' ' + option.lastname
      }
      const displayText = `${shouldBeVendorNeutral ? 'Report' : 'Appraisal'} downloaded by ${name}`
      buildingConsumerEvents.push({
        notification_type: displayText,
        created: event.time,
      })
    })
  }

  return buildingConsumerEvents
}

function process_events(
  input_events,
  orderData,
  shouldGetReminders,
  shouldGetStatuses,
  shouldGetMessages,
  is_amc_lender,
  refreshOrder,
) {
  const final_events = []

  input_events.forEach((e, eventIndex) => {
    if (
      e.senderName ||
      e.id === 'current_vendor_message' ||
      e.id === 'current_vendor_messages'
    ) {
      final_events.push(e)
    } else {
      const timeline_event = createAMCTimeline(
        e,
        orderData,
        shouldGetReminders,
        shouldGetStatuses,
        shouldGetMessages,
        is_amc_lender,
        refreshOrder,
        eventIndex
      )

      if (timeline_event) {
        if (final_events.length) {
          if (Array.isArray(final_events[final_events.length - 1])) {
            final_events[final_events.length - 1].push(timeline_event)
          } else {
            final_events.push([timeline_event])
          }
        } else {
          final_events.push([timeline_event])
        }
      }
    }
  })

  return final_events
}

const shouldShowAppraiserOverrideWarning = (orderData) => {
  let showOverrideWarning = false
  let hasAppraiserOverrides = false
  let isFindingAppraisersOrSchedulingInspection = false
  const { statusKey, products } = orderData

  if (statusKey === STATUS_KEY_FINDING_APPRAISERS || statusKey === STATUS_KEY_SCHEDULING_INSPECTION) {
    isFindingAppraisersOrSchedulingInspection = true
  }

  products.forEach((product) => {
    if (product.has_appraiser_overrides) {
      hasAppraiserOverrides = true
    }
  })

  if (hasAppraiserOverrides && isFindingAppraisersOrSchedulingInspection) {
    showOverrideWarning = true
  }

  return showOverrideWarning
}

const getNewMessageContainer = (
  orderData,
  props,
  shouldGetMessages,
  textToInsert,
  setTextToInsert,
  vendorMessages,
  cantMessage,
  primaryConversation,
  selectedTemplate,
  setSelectedTemplate,
  onHide,
  refreshOrder,
  isUnifiedConversation,
) => {
  const {
    newMessage,
    is_amc_lender,
    onFocus,
    primaryConversationType,
    allowMessageParticipation,
    activeOrderTab,
    primaryOrderId,
    amc_conversation,
    conversation,
  } = props

  let conversationType = primaryConversationType
  let conversationId
  let conversationCompany

  if (primaryConversation && primaryConversation.company) {
    conversationCompany = primaryConversation.company
  }

  if (conversationType === 'amc_conversation') {
    conversationId = amc_conversation ? amc_conversation.id : null
  } else if (conversationType === 'conversation' || conversationType === 'unified_conversation') {
    conversationId = conversation ? conversation.id : null
  } else {
    conversationId = null
  }

  if (vendorMessages) {
    conversationType = 'current_vendor_messages'
    conversationId = null
  } else if (!allowMessageParticipation || !shouldGetMessages ||
    !primaryConversationType || !conversationId) {
    return null
  }
  // if user can't send messages then disable new message container and return empty element
  if (cantMessage) {
    return <></>
  } else {
    return (
      <ChatSelector
        conversation_id={conversationId}
        conversationType={conversationType}
        conversationCompany={conversationCompany}
        is_revision={false}
        modal={false}
        subOrderId={activeOrderTab !== primaryOrderId && activeOrderTab}
        textToInsert={textToInsert}
        setTextToInsert={setTextToInsert}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        newMessageTitle="Conversation"
        newMessage={newMessage}
        is_amc_lender={is_amc_lender}
        onFocus={onFocus}
        orderData={orderData}
        resetTextToInsert={() => setTextToInsert('')}
        allowMessageParticipation={allowMessageParticipation}
        shouldGetMessages={shouldGetMessages}
        primaryConversationType={primaryConversationType}
        primaryConversation={primaryConversation}
        onHide={onHide}
        refreshOrder={refreshOrder}
        isUnifiedConversation={isUnifiedConversation}
        isTemplateSuggestionEnabled={true}
        audienceFilter={getAudienceFilter({
          conversationType,
          isAmcLender: is_amc_lender,
          isOrderDetails: true,
        })}
      />
    )
  }
}

function OrderFeeDisplay({ orderData, amount }) {
  // Check if we're waiting for fee information
  const isFeePending = orderData.fields_awaiting_consumers && (
    orderData.fields_awaiting_consumers.includes('integration_fee') ||
    orderData.fields_awaiting_consumers.includes('order_created')
  )

  // If pending, show "Pending" message
  if (isFeePending) {
    return <div className="user-info__texr">Pending</div>
  }

  // Otherwise show the fee amount
  return (
    <div className="user-info__texr">
      ${amount.toFixed(2)}
    </div>
  )
}

class OrderDetailsTab extends Component {
  state = {
    filters: [],
    textToInsert: '',
    selectedTemplate: null,
    isInspectionAvailabilityModalVisible: false,
  }

  setTextToInsert = (textToInsert, type) => {
    this.setState({ textToInsert })

    // Only clear selectedTemplate if user is typing manually
    // For users with approval permission, allow editing pre-approved templates
    if (type === 'manual') {
      const { selectedTemplate } = this.state
      const userCanApproveMessage = canApproveConversationMessages()
      if (shouldClearSelectedTemplate({ selectedTemplate, userCanApproveMessage, textToInsert })) {
        this.setState({ selectedTemplate: null })
      }
      // Otherwise, keep selectedTemplate for non-pre-approved templates or pre-approved templates (if user can approve) so user can edit
    }
  }

  handleTemplateClose = () => {
    this.setTextToInsert('')
    this.setSelectedTemplate(null)
  }

  onFilterClick = (filter, check) => {
    let new_filters = [...this.state.filters]

    if (!check) {
      new_filters.splice(new_filters.indexOf(filter), 1)
    } else {
      new_filters = [...new_filters, filter]
    }

    this.setState({
      filters: new_filters,
    })
  }

  clearFilters = () => {
    this.setState({
      filters: [],
    })
  }

  getAmcManagementFeeToDisplay = (amcManagementFee) => {
    if (amcManagementFee === 0) {
      return '$0.00'
    } else {
      return `$${amcManagementFee.toFixed(2)}`
    }
  }

  getPanelistFirmNames = (firmNames) => {
    return firmNames && Array.isArray(firmNames)
      ? (<div className='order-details-branch-name-popover'>
        {
          firmNames.map((firmName) => { return <div key={firmName}>{firmName}</div> })
        }
      </div>
      )
      : <></>
  }

  setSelectedTemplate = (selectedTemplate) => {
    this.setState({ selectedTemplate })
  }

  getBranchName = (branchData) => {
    return (<div className="user-info__texr">
      {checkPermission('branch_edit') ? (
        <Link to={`/branches/${branchData.id}`}>
          {branchData.name}
        </Link>
      ) : (
        branchData.name
      )}
    </div>)
  }

  componentDidMount() {
    this.fetchAvailabilityIfNeeded()
  }

  componentDidUpdate(prevProps) {
    const { orderData } = this.props
    if (prevProps.orderData?.id !== orderData?.id) {
      this.fetchAvailabilityIfNeeded()
    }
  }

  fetchAvailabilityIfNeeded = () => {
    const { orderData, dispatch } = this.props
    const isStreamlinedInspectionOrCompletionReport = isStreamlinedInspectionOrCompletionReportOrder(orderData)

    if (isStreamlinedInspectionOrCompletionReport && orderData?.id) {
      dispatch(fetchInspectionAvailability(orderData.id))
    }
  }

  formatAvailability = (availabilityData) => {
    if (!availabilityData?.inspection_availability || availabilityData.inspection_availability.length === 0) {
      return ''
    }

    const timezone = availabilityData.timezone

    // Create a copy of the array before sorting (Redux state is immutable)
    const formatted = [...availabilityData.inspection_availability]
      .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf())
      .map((item) => {
        const start = convertUTCToLocalTime(item.start, timezone)
        const end = convertUTCToLocalTime(item.end, timezone)

        // Format: "M/D H-H AM/PM" (e.g., "11/4 3-5PM")
        // Hours only, no minutes, AM/PM shown once at the end
        const dateStr = start.format('M/D')
        const startHour = start.format('h')
        const endHour = end.format('h')
        const ampm = end.format('A')

        if (start.isSame(end, 'day')) {
          // Same day: "11/4 3-5PM"
          return `${dateStr} ${startHour}-${endHour}${ampm}`
        } else {
          // Cross-day slot: "11/4 3PM - 11/5 6AM" (show both dates and times)
          const endDateStr = end.format('M/D')
          const startAmpm = start.format('A')
          const endAmpm = end.format('A')
          return `${dateStr} ${startHour}${startAmpm} - ${endDateStr} ${endHour}${endAmpm}`
        }
      })
      .join(' - ')

    return formatted
  }

  render() {
    const {
      orderData,
      is_amc_lender,
      primaryMessages,
      pastAppraiserMessages,
      currentUserId,
      timelineData,
      timelineLoading,
      branchData,
      refreshOrder,
      should_show_consumer_payment_style_field,
      is_review_appraiser,
      primaryMessagesBubbles,
      conversationStatus,
      primaryConversationType,
      allowMessageParticipation,
      onHide,
      amc_conversation,
      conversation,
      flags,
      inspectionAvailabilityData,
    } = this.props
    const { filters } = this.state
    const isCascade = orderData.order_request_method === REQUEST_METHODS.CASCADE
    const isBroadcast = orderData.order_request_method === REQUEST_METHODS.BROADCAST
    const isIndividually = orderData.order_request_method === REQUEST_METHODS.INDIVIDUALLY

    const display_filters = primaryMessages && primaryMessages.length
      ? [MESSAGE_FILTER, STATUS_FILTER, REMINDER_FILTER]
      : [STATUS_FILTER, REMINDER_FILTER]

    const isPDROrStreamlined = isPdrOrStreamlinedInspectionOrder(orderData)
    const isStreamlinedInspectionOrCompletionReport = isStreamlinedInspectionOrCompletionReportOrder(orderData)
    const isAMC = orderData.job_type === JOB_TYPES.AMC
    const shouldBeVendorNeutral = isPDROrStreamlined || flags?.vendorNeutralTimelineEvents
    const showInspectionAvailability = flags?.showInspectionsTab
    const streamlinedInspectionWorkflow = isStreamlinedInspectionOrCompletionReport && showInspectionAvailability
    const canEditInspectionDate = (
      flags?.enableVaOrderDueAndInspectionDate && orderData.job_type === 'va'
    ) || (streamlinedInspectionWorkflow && orderData.status_key !== STATUS_KEY_FINDING_APPRAISERS)

    const activeAssignmentStatuses = new Set(['active', 'company_accepted', 'accepted'])
    const activeAssignments = orderData?.assignment_list.filter(a => activeAssignmentStatuses.has(a.status))
    const isUnifiedConversation = primaryConversationType === 'unified_conversation'
    const jobTypeList = flags?.enableVaOrderDueAndInspectionDate ? ['review'] : ['va', 'review']

    const canMessage = (
      // if the conversation type and the jobtype is amc allow messages from the begining
      (primaryConversationType === 'amc_conversation' && isAMC) ||
      // If this is an AMP order with unified conversation, allow messages
      (isAMC && isUnifiedConversation) ||
      // timeline conversation can only be used when there is one active assignment
      // conversations with multiple companies are managed on the Appraisal Requests tab (i.e. broadcast)
      activeAssignments?.length === 1 ||
      // If there are no active assignments, allow messages on unified conversations
      (!activeAssignments?.length && isUnifiedConversation) ||
      // If the user has permission to send messages to the current vendor, allow messages
      (!activeAssignments?.length && checkPermission('enable_current_vendor_message'))
    )

    const cantMessage = !canMessage

    let primaryConversation
    if (primaryConversationType === 'amc_conversation') {
      primaryConversation = amc_conversation || null
    } else if (primaryConversationType === 'conversation' || isUnifiedConversation) {
      primaryConversation = conversation || null
    } else {
      primaryConversation = null
    }

    let forms = ''
    if (orderData && orderData.products) {
      orderData.products.forEach((product) => {
        if (product.included_forms) {
          product.included_forms.forEach((form) => {
            forms += `${forms.length ? ', ' : ''}${FORM_KEYS_TO_READABLE[form]}`
          })
        }
        if (product.unsupported_forms) {
          forms += `${forms.length ? ', ' : ''}${product.unsupported_forms}`
        }
      })
    }

    let shouldGetMessages = true
    let shouldGetReminders = true
    let shouldGetStatuses = true
    const shouldGetVendorNotes = true
    const vendorMessages =
      orderData &&
      orderData.statusKey === 'waiting_for_payment' &&
      checkPermission('enable_current_vendor_message') &&
      !isAMC &&
      !isUnifiedConversation
    // If we have filters, only get the things that are in the filter.
    // TODO: How do we classify the consumer event with the filters?
    if (filters.length) {
      if (filters.indexOf(MESSAGE_FILTER) === -1) {
        shouldGetMessages = false
      }
      if (filters.indexOf(REMINDER_FILTER) === -1) {
        shouldGetReminders = false
      }
      if (filters.indexOf(STATUS_FILTER) === -1) {
        shouldGetStatuses = false
      }
    }

    // TODO: There is still something wrong with this in the context of AMC, test to make sure that it works for the amc lender and
    // nom amc lender.
    let feed_messages = []

    if (shouldGetMessages && primaryMessages && primaryMessages.length) {
      feed_messages = primaryMessages
    }

    if (
      shouldGetMessages &&
      pastAppraiserMessages &&
      pastAppraiserMessages.length
    ) {
      feed_messages = [...feed_messages, ...pastAppraiserMessages]
    }

    // Create waiting for payment current_vendor_messages
    const currentVendorMessages = (orderData.current_vendor_messages || []).filter(message => !message.is_amc)
    if (shouldGetVendorNotes && currentVendorMessages.length) {
      const isPastAppraiserConversation = false

      const { messages } = prepConversationMessages(
        currentVendorMessages,
        null,
        currentUserId,
        'lender',
        isPastAppraiserConversation,
        orderData,
      )

      feed_messages = [...feed_messages, ...messages]
    }

    let timelineEvents = []
    if (timelineData) {
      timelineEvents = timelineData
    }

    if (orderData && !is_amc_lender) {
      timelineEvents = [
        ...timelineEvents,
        ...getDeliveryTimelineEvents(orderData, shouldBeVendorNeutral),
      ]
    }

    // Grab all timeline items and sort them by date
    let total_chat_timeline = []
    if (timelineEvents) {
      total_chat_timeline = [...feed_messages, ...timelineEvents]
    } else {
      total_chat_timeline = [...feed_messages]
    }

    total_chat_timeline.sort(dateSort)

    // If we have an array for messages, then that means it was initialized from a converation,
    // but we dont actually have any messages.
    if (
      shouldGetMessages &&
      Array.isArray(primaryMessages) &&
      !primaryMessages.length
    ) {
      total_chat_timeline.push({
        notification_type: 'initial_message',
      })
    }

    // Process all of the OrderTimeline Items (chat, events, vendor notes/messages)
    total_chat_timeline = process_events(
      total_chat_timeline,
      orderData,
      shouldGetReminders,
      shouldGetStatuses,
      shouldGetMessages,
      is_amc_lender,
      refreshOrder,
    )

    // Branch name section
    let displayBranchName
    if (branchData && branchData.name) {
      if (orderData && is_amc_lender && 'preferred_panel' in orderData) {
        // Preferred panel info for AMP users
        const panelistFirmNames = this.getPanelistFirmNames(orderData.preferred_panelist_firm_names)
        displayBranchName = (<div className="user-info">
          <div className="user-info__heading">Branch Name:<span className='text-danger'>*</span></div>
          <Popover
            content={panelistFirmNames}
            title={`Preferred Panel Strict: ${orderData.preferred_panel.panel_strict ? 'True' : 'False'}`}
            trigger="hover">
            {this.getBranchName(branchData)}
          </Popover>
        </div>)
      } else {
        displayBranchName = (<div className="user-info">
          <div className="user-info__heading">Branch Name:</div>
          {this.getBranchName(branchData)}
        </div>)
      }
    }

    if (!orderData) {
      return <div>Loading</div>
    }

    const reggora_amc_management_fee = orderData.reggora_amc_management_fee_dollars === null
      ? 0
      : orderData.reggora_amc_management_fee_dollars

    const getRequestedAppraiserElement = () => {
      if (isAMC && !is_amc_lender) {
        // If we are dealing with an amc order, then we only actually want to show
        // the requested appraiser if the user is the amc lender.
        return 'Reggora Network'
      }
      const requested = (orderData || {}).requested || []

      if (isAMC && isIndividually && is_amc_lender && requested[0]) {
        return requested[0].panelist_name
      }
      if (!isAMC && isIndividually && requested[0]) {
        return requested[0].panelist_name
      }
      if (!isAMC && isIndividually && requested.length === 0) {
        return <Rephrase>Calculating Appraiser</Rephrase>
      }
      if (isBroadcast) {
        return 'Broadcast'
      }
      if (isCascade) {
        return 'Cascade'
      }

      return <></>
    }

    return (
      <Fragment>
        <div className="d-flex">
          <div className="details-container p-4">
            <h4 className="lead">
              <ProfileOutlined /> Order Details
            </h4>

            <div className="user-info">
              <div className="user-info__heading">Status:</div>
              <div className="user-info__texr">
                {getOrderStatus(orderData, is_amc_lender)}
              </div>
            </div>

            {orderData.va_lin && (
              <div className="user-info">
                <div className="user-info__heading">VA Portal LIN:</div>
                <div className="user-info__texr">{orderData.va_lin}</div>
              </div>
            )}

            {!jobTypeList.includes(orderData.job_type) && (
              <Fragment>
                <div className="user-info">
                  <div className="user-info__heading">{is_amc_lender ? 'Lender ' : ''}Due Date:</div>
                  <div className="user-info__texr">
                    {getUTCTime(orderData.lender_due_date || orderData.due_date)}
                  </div>
                </div>
                {is_amc_lender && (
                  <div className="user-info">
                    <div className="user-info__heading">Appraiser Due Date:</div>
                    <div className="user-info__texr">
                      {getUTCTime(orderData.due_date)}
                    </div>
                  </div>
                )}
              </Fragment>
            )}
            {canEditInspectionDate ? (
              <>
                <div className="user-info">
                  <div className="user-info__heading">Inspection Date:</div>
                  <div className="user-info__texr">
                    {streamlinedInspectionWorkflow
                      ? getInspectionDateAndTime(orderData.inspection_date, orderData.timezone)
                      : getUTCTime(orderData.inspection_date)
                    }
                    <ModalTrigger
                      component={ModalSetInspectionDate}
                      showOnlyDate={!streamlinedInspectionWorkflow}
                      orderData={orderData}
                      refreshOrder={refreshOrder}
                      backdrop='static'
                    >
                      <Button type="link">
                        <i className="mr-2 fa fa-calendar" aria-hidden="true"></i>
                        Edit
                      </Button>
                    </ModalTrigger>
                  </div>
                </div>
              </>
            ) : (
              orderData.inspection_date && (
                <div className="user-info">
                  <div className="user-info__heading">Inspection Date:</div>
                  <div className="user-info__texr">{utcToLocal(orderData.inspection_date).format('LLLL')}</div>
                </div>)
            )}

            {streamlinedInspectionWorkflow && (
              <div className="user-info">
                <div className="user-info__heading">Inspection Availability:</div>
                <div className="user-info__texr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{inspectionAvailabilityData ? this.formatAvailability(inspectionAvailabilityData) : ''}</span>
                  <Button
                    type="link"
                    onClick={() => this.setState({ isInspectionAvailabilityModalVisible: true })}
                  >
                    <i className="mr-2 fa fa-calendar" aria-hidden="true"></i>
                    Edit
                  </Button>
                </div>
              </div>
            )}

            {[
              STATUS_KEY_FINDING_APPRAISERS,
              STATUS_KEY_SCHEDULING_INSPECTION,
            ].includes(orderData.statusKey) && checkCanViewAppraiserNameOnOrder(orderData) && (
              <div className="user-info">
                <>
                  <div className="user-info__heading"><Rephrase>Requested Appraiser:</Rephrase></div>
                  <div className="user-info__texr"> {getRequestedAppraiserElement()}</div>
                </>
              </div>
            )}
            {['inspection_scheduled', 'inspection_completed', 'submitted'].indexOf(orderData.statusKey) > -1 && orderData.accepted && checkCanViewAppraiserNameOnOrder(orderData) ? (<div className="user-info">
              <div className="user-info__heading"><Rephrase>Appraiser:</Rephrase></div>
              <div className='user_info__texr'>{orderData.accepted.panelist_name}</div>
            </div>) : null}
            <div className="user-info">
              <div className="user-info__heading">Order Type:</div>
              <div className="user-info__texr">{orderData.priority}</div>
            </div>
            <div className="user-info">
              <div className="user-info__heading">Product Names:</div>
              <div className="product-details">
                {orderData.products &&
                  orderData.products.map((product) => {
                    return (
                      <div key={product.id} className="user-info__texr">
                        <li className="product-name">{product.description}</li>
                      </div>
                    )
                  })}
              </div>
            </div>
            {should_show_consumer_payment_style_field &&
              orderData.job_type !== 'review' && (
              <div className="user-info">
                <div className="user-info__heading">
                    Borrower Payment Option:
                </div>
                <div className="user-info__texr user-info-longText">
                  <div className="user-info__texr">
                    {
                      paymentOptionsToReadable[
                        orderData.consumer_payment_style
                      ]
                    }
                  </div>
                </div>
              </div>
            )}
            {Boolean(orderData.order_fee_for_lender) &&
              !is_review_appraiser &&
              orderData.job_type !== 'review' && (
              <>
                {/* Show Total Fee on the top for AMC Lender only */}
                {is_amc_lender && (
                  <div className="user-info">
                    <div className="user-info__heading">
                        Total Fee:
                    </div>
                    <OrderFeeDisplay
                      orderData={orderData}
                      amount={orderData.total_lender_fee}
                    />
                    {shouldShowAppraiserOverrideWarning(orderData) && (
                      <Popover
                        content="This order has one or more products with vendor level pricing associated with it. Please note that the fee may vary depending on which vendor accepts the order."
                        overlayStyle={{ width: 350 }}
                      >
                        <span className="fee-warning ml-2 pl-2 pr-2">
                          <i className="fas fa-exclamation-circle exlamation-color mr-1"></i>
                              Fee may vary
                        </span>
                      </Popover>
                    )}
                  </div>
                )}
                {/* Show Base Order Fee for non AMC lender */}
                {!is_amc_lender && (
                  <div className="user-info">
                    <div className="user-info__heading">
                        Order Fee:
                    </div>
                    <OrderFeeDisplay
                      orderData={orderData}
                      amount={orderData.order_fee_for_lender}
                    />
                    {shouldShowAppraiserOverrideWarning(orderData) && (
                      <Popover
                        content="This order has one or more products with vendor level pricing associated with it. Please note that the fee may vary depending on which vendor accepts the order."
                        overlayStyle={{ width: 350 }}
                      >
                        <span className="fee-warning ml-2 pl-2 pr-2">
                          <i className="fas fa-exclamation-circle exlamation-color mr-1"></i>
                              Fee may vary
                        </span>
                      </Popover>
                    )}
                  </div>
                )}
              </>
            )}
            {/* Show Appraiser Fee and AMC Fee for AMC lender */}
            {is_amc_lender && !is_review_appraiser ? (
              <Fragment>
                <div className="user-info">
                  <div className="user-info__heading">Appraiser Fee:</div>
                  <div className="user-info__texr">${orderData.total_appraiser_fee.toFixed(2)}</div>
                </div>
                <div className="user-info">
                  <div className="user-info__heading" data-testid='amcManagementFeeHeader'>
                    AMC Fee:
                  </div>
                  <div className="user-info__texr" data-testid='amcManagementFeeBody'>
                    {orderData.is_reggora_amc_order_with_standard_products &&
                    this.getAmcManagementFeeToDisplay(reggora_amc_management_fee)}
                    {!!orderData.reggora_fee && `$${orderData.reggora_fee.toFixed(2)}`}
                  </div>
                  {orderData.is_reggora_amc_order_with_standard_products &&
                    <ModalTrigger
                      modalClassName='modal-primary'
                      component={EditAMCManagementFeeModal}
                      totalFee={orderData.total_lender_fee}
                      appraiserFee={orderData.total_appraiser_fee}
                      amcManagementFee={reggora_amc_management_fee}
                      orderId={orderData.id}
                      refreshOrder={refreshOrder}
                    >
                      <i
                        id='priority-popover'
                        className='edit-icon far fa-edit margin-top-3'
                        data-testid='amcManagementFeeEditIcon' />
                    </ModalTrigger>}
                  {!orderData.is_reggora_amc_order_with_standard_products &&
                    <span className='ml-2'><i>Editing mgt fee is disabled for legacy AMC flow</i></span>
                  }
                </div>
                {orderData.is_reggora_amc_order_with_standard_products && (
                  <div className="user-info">
                    <div className="user-info__heading">Vendor Fee Override:</div>
                    <div className="user-info__texr">
                      {orderData.vendor_fee_override_dollars != null
                        ? `$${orderData.vendor_fee_override_dollars.toFixed(2)}`
                        : 'Not set'}
                    </div>
                    <ModalTrigger
                      component={EditVendorFeeOverrideModal}
                      vendorFeeOverrideDollars={orderData.vendor_fee_override_dollars}
                      orderId={orderData.id}
                      refreshOrder={refreshOrder}
                      modalClassName='modal-primary'
                    >
                      <i className='edit-icon far fa-edit margin-top-3' />
                    </ModalTrigger>
                  </div>
                )}
              </Fragment>
            ) : null}
            {/* Show Additional Fees */}
            {orderData.additional_fees &&
              !is_review_appraiser &&
              [...orderData.additional_fees.map((fee, index) => {
                return (
                  <div className="user-info" key={`additional-fee-${fee.id || index}`}>
                    <div className="user-info__heading">Additional Fee:</div>
                    <div className="user-info__texr">
                      {formatCurrency(fee.amount)} - {fee.description}
                    </div>
                  </div>
                )
              }),
              ...orderData.order_fees.map((fee, index) => {
                return (
                  <div className="user-info" key={`order-fee-${fee.id || index}`}>
                    <div className="user-info__heading">Additional Fee:</div>
                    <div className="user-info__texr">
                      {formatCurrency(fee.fee_amount / 100)} - {fee.fee_description}
                    </div>
                  </div>
                )
              }),
              ]
            }
            {/* Show Total Fee at bottom for non-AMC lender only when there are additional fees */}
            {!is_amc_lender &&
              !is_review_appraiser &&
              orderData.job_type !== 'review' &&
              ((orderData.additional_fees && orderData.additional_fees.length > 0) ||
               (orderData.order_fees && orderData.order_fees.length > 0)) && (
              <div className="user-info">
                <div className="user-info__heading">
                    Total Fee:
                </div>
                <OrderFeeDisplay
                  orderData={orderData}
                  amount={orderData.total_lender_fee}
                />
              </div>
            )}
            <div className="user-info">
              <div className="user-info__heading">Forms:</div>
              <div className="user-info__texr">{forms}</div>
            </div>
            {orderData.status === 'Done' && (
              <div className="user-info">
                <div className="user-info__heading">
                  <Rephrase>Sent Appraisal To Consumer:</Rephrase>
                </div>
                {orderData.sent_to_consumer &&
                  orderData.sent_to_consumer.map((consumer, index) => (
                    <div className="user-info__texr" key={index}>
                      {consumer.name} Sent{' '}
                      {utcToLocal(consumer.time).format('MMM Do, YYYY')}
                    </div>
                  ))}
              </div>
            )}
            {is_amc_lender && orderData.lender && (
              <div className="user-info">
                <div className="user-info__heading">Lender:</div>
                <div className="user-info__texr">{orderData.lender.name}</div>
              </div>
            )}
            {displayBranchName}
            {is_amc_lender && (
              <>
                <div className="user-info">
                  <div className="user-info__heading">RUCC Code:</div>
                  <div className="user-info__texr">{this.props.ruccCode}</div>
                </div>
                <div className="user-info">
                  <div className="user-info__heading">RUCC Classification:</div>
                  <div className="user-info__texr">{getRUCCTypeFromCode(this.props.ruccCode)}</div>
                </div>
              </>
            )}
            <div className="user-info">
              <div className="user-info__heading pt-2">Assigned:</div>
              {orderData.fields_awaiting_consumers &&
                orderData.fields_awaiting_consumers.includes(
                  'order_created',
                ) && <div className="user-info__texr">Pending</div>}
              <div className="user-info__text">
                {(!orderData.fields_awaiting_consumers ||
                  !orderData.fields_awaiting_consumers.includes(
                    'order_created',
                  )) &&
                  buildAvatars(orderData.assigned)}
                {checkPermission('order_edit_assigned_users') &&
                (!orderData.fields_awaiting_consumers ||
                  !orderData.fields_awaiting_consumers.includes(
                    'order_created',
                  )) && ( // Only show the edit processors to lender admins, and dont show it to the AMC
                  <ModalTrigger
                    component={EditAssignedModal}
                    orderData={orderData}
                    id={orderData.id}
                    refreshOrder={refreshOrder}
                  >
                    <Button color="light">Edit</Button>
                  </ModalTrigger>
                )}
              </div>
            </div>
          </div>
          {checkPermission('notes_view') && orderData ? (
            <div className="internal-note-container p-4">
              <h4 className="lead">
                <FormOutlined /> Internal Notes
              </h4>
              <Notes
                notes={orderData.notes}
                currentUserId={currentUserId}
                orderId={orderData.id}
                refreshOrder={refreshOrder}
                flags={flags}
              />
            </div>
          ) : (
            <div className="notes-container-placeholder w-50"></div>
          )}
        </div>
        <div className="pt-0 pb-4 px-4">
          <h4 className="lead">
            <BarsOutlined /> Order Timeline
          </h4>
          <div className="d-flex flex-column align-content-between">
            <div className="chat-controls">
              {primaryMessagesBubbles && (
                <div className="chat-avatars">
                  {primaryMessagesBubbles}
                </div>
              )}
              <div className="filters">
                <h6 className="filter-header">Filters:</h6>
                {display_filters.map((tag) => (
                  <CheckableTag
                    className=""
                    key={tag}
                    checked={filters.indexOf(tag) > -1}
                    onChange={(checked) => this.onFilterClick(tag, checked)}
                  >
                    {tag}
                  </CheckableTag>
                ))}
                {filters.length > 0 && (
                  <span className="filter-clear" onClick={this.clearFilters}>
                    Clear
                  </span>
                )}
              </div>
            </div>

            <div className="grey-divider"></div>

            {timelineData &&
            !timelineLoading &&
            conversationStatus !== 'loading' ? (
                <ChatWrapper
                  total_chat_timeline={total_chat_timeline}
                  timelineData={timelineData}
                  isUnifiedConversation={isUnifiedConversation}
                  isAMPUser={is_amc_lender}
                  customFieldLabels={this.props.customFieldLabels}
                />
              ) : (
                <SmallLoader />
              )
            }

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
              {(primaryConversationType || vendorMessages) &&
              getNewMessageContainer(
                orderData,
                this.props,
                shouldGetMessages,
                this.state.textToInsert,
                this.setTextToInsert,
                vendorMessages,
                cantMessage,
                primaryConversation,
                this.state.selectedTemplate,
                this.setSelectedTemplate,
                onHide,
                refreshOrder,
                isUnifiedConversation,
              )}
            </div>

            <UnansweredMessageInfo
              primaryConversation={primaryConversation || {}}
              is_amc_lender={is_amc_lender}
              shouldGetMessages={shouldGetMessages}
            />

            {!flags?.useUpdatedMessageTemplatesUx && allowMessageParticipation &&
              shouldGetMessages &&
              primaryConversationType &&
              primaryConversation &&
              !orderData.has_been_reassigned && (
              <CommentTemplateDropdown
                name={'order-messages-templates-list'}
                onHide={onHide}
                refreshOrder={refreshOrder}
                setTextToInsert={this.setTextToInsert}
                setSelectedTemplate={this.setSelectedTemplate}
              />
            )}
          </div>
        </div>
        <InspectionAvailabilityModal
          visible={this.state.isInspectionAvailabilityModalVisible}
          onCancel={() => this.setState({ isInspectionAvailabilityModalVisible: false })}
          orderData={orderData}
        />
      </Fragment>
    )
  }
}

const mapStateToProps = (state) => {
  const lenderData = state.resource?.user?.data?.lender || {}
  const customFieldLabels = (lenderData.custom_los_fields || []).reduce((acc, field) => {
    if (field.mapping_field_name && field.field_name) {
      acc[field.mapping_field_name] = field.field_name
    }
    return acc
  }, { ...buildFieldLabelMap() })

  return {
    inspectionAvailabilityData: selectInspectionAvailabilityData(state),
    customFieldLabels,
  }
}

export default connect(mapStateToProps)(withLDConsumer()(OrderDetailsTab))
